/*
PURPOSE: Magic-link verification landing (/auth/verify) — verifies the token in
the URL and redirects on success.
DOMAIN BOUNDARIES:
- Presentation is the editorial design system (warm ivory ground, Cormorant
  display heading), consistent with the migrated public pages. No coral-reef
  illustration.
*/
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import Spinner from '../../../components/Spinner/Spinner';
import { DisplayHeading } from '../../../components/editorial';
import EditorialAuthShell from '../../../components/EditorialAuthShell/EditorialAuthShell';
import { AuthContext } from '../../../components/Auth/AuthContext/AuthContext';
import logger from '../../../utils/logger';
import { getAndClearLoginRedirectUrl } from '../../../utils/authUtils';

const VerifyMagicLink: React.FC = () => {
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicLink } = useAuth();
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    const verifyLink = async () => {
      const email = searchParams.get('email');
      const token = searchParams.get('token');

      if (!email || !token) {
        setError('Invalid magic link');
        setVerifying(false);
        return;
      }

      try {
        const success = await loginWithMagicLink(email, token);
        if (success) {
          const redirectUrl = getAndClearLoginRedirectUrl() || '/dashboard';
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1000);
        } else {
          setError('Verification failed');
          setVerifying(false);
        }
      } catch (error) {
        logger.error('Error during magic link verification:', error);
        setError('Verification failed');
        setVerifying(false);
      }
    };

    verifyLink();
  }, [searchParams, navigate, loginWithMagicLink, currentUser]);

  return (
    <EditorialAuthShell bloomSeed={7}>
      {verifying ? (
        <>
          <DisplayHeading as="h1" scale="heading" measure={false} className="text-center">
            Signing you in
          </DisplayHeading>
          <p className="mt-4 text-editorial-body text-editorial-muted/70">
            Hold tight, we&apos;re verifying your magic link.
          </p>
          <div className="mt-8">
            <Spinner />
          </div>
        </>
      ) : error ? (
        <>
          <DisplayHeading as="h1" scale="heading" measure={false} className="text-center">
            Verification failed
          </DisplayHeading>
          <p className="mt-4 text-editorial-body text-editorial-muted/70">
            {error}. This link may have expired or already been used.
          </p>
        </>
      ) : (
        <>
          <DisplayHeading as="h1" scale="heading" measure={false} className="text-center">
            You&apos;re in
          </DisplayHeading>
          <p className="mt-4 text-editorial-body text-editorial-muted/70">
            Taking you to your dashboard.
          </p>
        </>
      )}
    </EditorialAuthShell>
  );
};

export default VerifyMagicLink;
